/**
 * Which deployment the browser is currently looking at, derived from the host.
 *
 * Deliberately host-based rather than a build-time flag: every environment is
 * built from the same command and told apart only by where it is served from
 * (`app.{env}.smart-cgt.com`), so a `VITE_` variable would have to be threaded
 * through six deploy workflows to say something the URL already says.
 *
 * Kept as a pure function in `src/lib` because `vitest.config.ts` is
 * `environment: 'node'` with `include: ['src/**\/*.test.ts']` — logic left in a
 * `.tsx` component could never be tested here.
 */

const DEV_HOST = 'app.dev.smart-cgt.com'
const STAGING_HOST = 'app.stag.smart-cgt.com'
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]'])

/**
 * True on the dev deployment, and when running locally.
 *
 * MATCHED EXACTLY, never by substring. `app.dev.smart-cgt.com` shares the
 * letters "dev" with nothing today, but the client hosts sit on the same domain
 * (`app.medstar-health`, `app.monte.stag`, `app.dhmc.stag`, `app.mskcc.stag`),
 * so a loose test here is one badly-named future environment away from leaking
 * an internal link onto a customer's screen.
 */
export function isDevDeployment(hostname: string): boolean {
  return hostname === DEV_HOST || LOCAL_HOSTS.has(hostname)
}

/** Which internal environment to name in the app shell, if any. */
export type DeployEnvironmentLabel = 'dev' | 'staging'

/**
 * The environment marker shown beside the logo, or null on production and on
 * every client deployment.
 *
 * This exists because the version number alone cannot answer "which environment
 * am I on": one shared counter is minted on staging as a release tag and dev
 * shows the newest one, so the two show the same number whenever dev holds
 * nothing unreleased (see `@/lib/buildIdentity`).
 *
 * MATCHED EXACTLY, for a sharper version of the reason isDevDeployment is: the
 * client hosts are app.monte.stag / app.dhmc.stag / app.mskcc.stag, so a
 * substring test for "stag" would label a customer's screen "staging".
 *
 * Kept separate from isDevDeployment rather than folded into it: that one
 * answers whether to advertise an internal link, and treating the two as the
 * same question would couple a visual label to a disclosure decision.
 */
export function deployEnvironmentLabel(hostname: string): DeployEnvironmentLabel | null {
  if (hostname === DEV_HOST || LOCAL_HOSTS.has(hostname)) return 'dev'
  if (hostname === STAGING_HOST) return 'staging'
  return null
}
